import { useEffect, useState } from "react";
import { Check, Clock, FileText, Globe, MessageSquare, Save } from "lucide-react";
import type { DeliveryChannel } from "@z-suite/shared";
import { useAuth } from "../lib/auth.js";
import { useProfile, useUpdateSettings } from "../lib/queries.js";
import { AppShell } from "../components/AppShell.js";
import { Button, Card, Field, FullPageSpinner } from "../components/ui.js";

const PLAN_LABELS: Record<string, string> = {
  trial: "Deneme",
  solo: "Solo",
  team: "Takım",
  enterprise: "Kurumsal",
};

export default function Settings() {
  const { user } = useAuth();
  const uid = user!.id;
  const { data: profile, isLoading } = useProfile(uid);
  const update = useUpdateSettings(uid);

  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("07:00");
  const [channels, setChannels] = useState<Set<DeliveryChannel>>(new Set(["web"]));
  const [slackUrl, setSlackUrl] = useState("");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Profil yüklenince formu doldur
  useEffect(() => {
    if (!profile) return;
    setFullName(profile.full_name ?? "");
    setCompany(profile.company ?? "");
    setRole(profile.role ?? "");
    setDeliveryTime((profile.delivery_time ?? "07:00").slice(0, 5));
    setChannels(new Set(profile.delivery_channels ?? ["web"]));
    setSlackUrl(profile.slack_webhook_url ?? "");
  }, [profile]);

  if (isLoading || !profile) {
    return (
      <AppShell>
        <FullPageSpinner label="Ayarlar yükleniyor…" />
      </AppShell>
    );
  }

  function toggleChannel(c: DeliveryChannel) {
    setChannels((prev) => {
      const next = new Set(prev);
      next.has(c) ? next.delete(c) : next.add(c);
      return next;
    });
  }

  async function save() {
    setError(null);
    setSaved(false);
    const slackOn = channels.has("slack");
    if (slackOn && slackUrl && !/^https:\/\/hooks\.slack\.com\//.test(slackUrl)) {
      setError("Slack webhook URL'si https://hooks.slack.com/ ile başlamalı.");
      return;
    }
    try {
      await update.mutateAsync({
        full_name: fullName.trim(),
        company: company.trim(),
        role: role.trim(),
        delivery_time: deliveryTime,
        delivery_channels: [...channels],
        slack_webhook_url: slackUrl.trim() || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError((e as Error).message ?? "Kaydedilemedi.");
    }
  }

  const slackSelected = channels.has("slack");

  return (
    <AppShell>
      <header className="mb-8 flex items-end justify-between">
        <div>
          <p className="eyebrow mb-2">Ayarlar</p>
          <h1 className="font-serif text-3xl text-slate-50">Hesap & teslimat</h1>
        </div>
        <span className="rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-xs text-gold-300">
          {PLAN_LABELS[profile.plan] ?? profile.plan} planı
        </span>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Profil */}
        <Card className="p-6">
          <h2 className="mb-4 font-serif text-lg text-slate-100">Profil</h2>
          <div className="space-y-4">
            <Field label="Ad Soyad" value={fullName} onChange={(e) => setFullName(e.target.value)} />
            <Field label="Şirket" value={company} onChange={(e) => setCompany(e.target.value)} />
            <Field label="Ünvan" value={role} onChange={(e) => setRole(e.target.value)} />
            <div>
              <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-slate-300">
                <Clock className="h-4 w-4 text-slate-400" /> Teslimat saati (TRT)
              </label>
              <input
                type="time"
                value={deliveryTime}
                onChange={(e) => setDeliveryTime(e.target.value)}
                className="w-full rounded-lg border border-slate-700/60 bg-ink-800/60 px-3.5 py-2.5 text-sm text-slate-100 focus:border-gold/50 focus:outline-none focus:ring-2 focus:ring-gold/40"
              />
            </div>
          </div>
        </Card>

        {/* Teslimat */}
        <Card className="p-6">
          <h2 className="mb-4 font-serif text-lg text-slate-100">Teslimat kanalları</h2>
          <div className="grid gap-2.5 sm:grid-cols-3">
            {(
              [
                { id: "web", label: "Web paneli", icon: Globe },
                { id: "slack", label: "Slack", icon: MessageSquare },
                { id: "pdf", label: "PDF rapor", icon: FileText },
              ] as const
            ).map(({ id, label, icon: Icon }) => {
              const on = channels.has(id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => toggleChannel(id)}
                  aria-pressed={on}
                  className={`flex flex-col items-start gap-1.5 rounded-xl border p-3.5 text-left transition-all ${
                    on ? "border-gold/60 bg-gold/10" : "border-slate-700/50 bg-ink-800/40 hover:border-slate-500/70"
                  }`}
                >
                  <Icon className={`h-5 w-5 ${on ? "text-gold" : "text-slate-400"}`} />
                  <span className={`text-sm ${on ? "text-white" : "text-slate-300"}`}>{label}</span>
                </button>
              );
            })}
          </div>

          {/* Slack webhook bağlama */}
          {slackSelected && (
            <div className="mt-5 space-y-2">
              <Field
                label="Slack Incoming Webhook URL"
                type="url"
                placeholder="https://hooks.slack.com/services/…"
                value={slackUrl}
                onChange={(e) => setSlackUrl(e.target.value)}
                hint="Slack → Apps → Incoming Webhooks ile oluşturun. Her sabah brifing bu kanala düşer."
              />
              {profile.slack_webhook_url && (
                <p className="flex items-center gap-1.5 text-xs text-emerald-400">
                  <Check className="h-3.5 w-3.5" /> Bir webhook bağlı.
                </p>
              )}
            </div>
          )}

          <p className="mt-4 text-xs text-slate-500">
            PDF teslimatı yakında (Faz 4). Web paneli her zaman aktiftir.
          </p>
        </Card>
      </div>

      <div className="mt-6 flex items-center gap-4">
        <Button onClick={save} loading={update.isPending}>
          <Save className="h-4 w-4" /> Kaydet
        </Button>
        {saved && (
          <span className="flex items-center gap-1.5 text-sm text-emerald-400">
            <Check className="h-4 w-4" /> Kaydedildi
          </span>
        )}
        {error && <span className="text-sm text-red-400">{error}</span>}
      </div>
    </AppShell>
  );
}
